import * as React from "react";
import { connect } from "react-redux";
import * as Chart from "../../ChartZone.styles";
import { Portfolio } from "../../../../context/reports/comfort/types";
import { DropdownMenu } from "@conserveit/blocks";
import { ORDER_BY_OPTIONS } from "../../../../context/reports/comfort/constants";
import { RankDir } from "../../../../context/reports/comfort/types";
import { ChartLoader } from "../../../Charts/components/ChartLoader";
import { useTrackedActions } from "../hooks/useTrackedActions";
import { NoResult } from "../../";
import { ComfortTrendBarChart } from "../../../Charts/components/ComfortTrendBarChart";
import { SiteComfortRankingBarChart } from "../../../Charts/components/SiteComfortRankingBarChart";
import { Series } from "../../../Reports/Utilities/types";
import { COLORS_RAW } from "../../../AppChrome/App.constants";
import { TopMargin, LeftMargin } from "../../../Layout/Page";
import moment from "moment";
import { getPortfolioChartConfig } from "../portfolioChartConfig";

const getDataForSingleSite = (portfolio?: Portfolio): Series[] => {
  if (!portfolio) return [];

  const series: Series[] = [];

  Object.keys(portfolio).forEach((key) => {
    if (portfolio[key].series.current.length > 0) {
      series.push({
        name: portfolio[key].name,
        data: portfolio[key].series.current.map((c) => [
          moment(c.date).valueOf(),
          c.totalPctComfort,
        ]),
      });
    }
  });
  return series;
};

const getDataForMultipleSites = (
  rankDir: RankDir,
  portfolio?: Portfolio
): { name: string; value: number }[] => {
  if (!portfolio) return [];

  const averageComfortBySite: { [key: string]: number } = {};

  Object.keys(portfolio).forEach((key) => {
    if (portfolio[key].series.current.length > 0) {
      const currentSeries = portfolio[key].series.current;
      const sumPctComfort = currentSeries.reduce(
        (acc, curr) => acc + curr.totalPctComfort,
        0
      );

      averageComfortBySite[key] = Math.round(
        sumPctComfort / currentSeries.length
      );
    }
  });

  const orderedKeys = Object.keys(averageComfortBySite).sort(
    (a, b) =>
      (rankDir == "worst" ? 1 : -1) *
      (averageComfortBySite[b] - averageComfortBySite[a])
  );

  return orderedKeys.map((key) => ({
    name: portfolio[key].name,
    value: averageComfortBySite[key],
  }));
};

interface Props {
  portfolio?: Portfolio;
  siteId: string;
  portfolioLoading: boolean;
  selectedSites: Insite.AppState["betaSelections"]["selectedSites"];
  rankDir: RankDir;
}

const SeriesFrame: React.FC<Props> = ({
  portfolio,
  portfolioLoading,
  selectedSites,
  rankDir,
}) => {
  const comfortTimeSeriesChartConfig = React.useMemo(() => {
    if (!portfolio) return undefined;
    return getPortfolioChartConfig(portfolio, rankDir, "", {
      from: "",
      to: "",
    });
  }, [portfolio, rankDir]);

  const trackingTitle = "Average building comfort during occupancy";

  const { trackedRankDir } = useTrackedActions();

  const isSingleSite = selectedSites.length === 1;
  const singleSiteSeries = isSingleSite ? getDataForSingleSite(portfolio) : [];
  const multiSiteData = !isSingleSite
    ? getDataForMultipleSites(rankDir, portfolio)
    : [];

  const hasData =
    comfortTimeSeriesChartConfig &&
    comfortTimeSeriesChartConfig.dataSource.categories[0].category.length > 0;

  return (
    <Chart.Container>
      <Chart.Block>
        <Chart.Header>
          <Chart.Heading>
            <Chart.Title>
              Site <em>comfort</em> performance trend
            </Chart.Title>
            <Chart.Subtitle>
              Showing average building <em>comfort</em> during occupancy
            </Chart.Subtitle>
          </Chart.Heading>
          <Chart.Controls>
            {selectedSites.length > 1 && (
              <DropdownMenu
                trigger="Order by"
                options={ORDER_BY_OPTIONS}
                onSelect={trackedRankDir(trackingTitle)}
                // onSelect={(value) => setRankDirLocal(value)}
                value={rankDir}
                right
              />
            )}
          </Chart.Controls>
        </Chart.Header>

        <Chart.Content>
          <TopMargin>
            {portfolioLoading ? (
              <ChartLoader />
            ) : comfortTimeSeriesChartConfig && hasData ? (
              isSingleSite ? (
                <LeftMargin>
                  <ComfortTrendBarChart
                    data={singleSiteSeries[0]?.data ?? []}
                    color={COLORS_RAW.GREEN_002}
                    yAxisTitle="% comfort during occupied hours"
                    caption="Daily building comfort during occupancy (zones within 2°C of set point, averaged across all zones)"
                  />
                </LeftMargin>
              ) : (
                <SiteComfortRankingBarChart
                  data={multiSiteData}
                  color={COLORS_RAW.GREEN_002}
                  xAxisTitle="% comfort during occupied hours"
                  caption="Daily building comfort during occupancy (zones within 2°C of set point, averaged across all zones)"
                />
              )
            ) : (
              <NoResult />
            )}
          </TopMargin>
        </Chart.Content>
      </Chart.Block>
    </Chart.Container>
  );
};

const mapStateToProps = ({ betaSelections, userSites }: Insite.AppState) => {
  const { selectedSites } = betaSelections;
  const { sites } = userSites;
  return { selectedSites, sites };
};

export default connect(mapStateToProps)(SeriesFrame);
